import { RSAKeyPair } from 'client/PasswordManager/PasswordManagerCryptoUtil';
import {
  get, keyBy, uniq, uniqBy,
} from 'lodash';
import CollectionModule from 'client/store/modules/CollectionModule';
import PasswordManagerBackupApi from 'client/api/v2/PasswordManagerBackupApi';
import PasswordManagerBackupKeyApi from 'client/api/v2/PasswordManagerBackupKeyApi';
import PasswordManagerDeviceApi from 'client/api/v2/PasswordManagerDeviceApi';
import PasswordManagerFilters from 'client/PasswordManager/PasswordManagerFilters';
import PasswordManagerRestoreRequestApi from 'client/api/v2/PasswordManagerRestoreRequestApi';
import PasswordManagerUserApi from 'client/api/v2/PasswordManagerUserApi';
import UserApi from 'client/api/v2/UserApi';
import Vue from 'vue';

export const PasswordManagerRestoreRequestCollection = (api = PasswordManagerRestoreRequestApi) => {
  const collection = new CollectionModule(api, 'id', '');
  const PasswordManagerRestoreRequestCollectionModule = collection.extractModule();

  const RESET_RESTORE_REQUEST_COLLECTION = 'RESET_RESTORE_REQUEST_COLLECTION';
  const SET_RESTORE_REQUEST_TOTAL_COUNT = 'SET_RESTORE_REQUEST_TOTAL_COUNT';
  const SET_SELECTED_BACKUP_KEYS_BY_NAME = 'SET_SELECTED_BACKUP_KEYS_BY_NAME';
  const SET_UPLOADED_PRIVATE_KEYS = 'SET_UPLOADED_PRIVATE_KEYS';
  const UPDATE_REFRESH_KEY = 'UPDATE_REFRESH_KEY';

  /**
   * Helper function to fetch and join remote data
   * @param {Array<any>} source
   * @param {Array<{
   *  sourceKey: string,
   *  primaryKey: string,
   *  fetcher: function,
   *  fieldName: string}>} mappers
   * @returns {Array<any>}
   */
  const mapData = async (source, mappers) => {
    const fetchers = mappers.map(({
      sourceKey, primaryKey, fetcher,
    }) => {
      // extract uniq sourceKey
      const uniqIds = uniq(
        source.map(record => record[sourceKey]),
      );

      // fetch and index data by primary key
      return fetcher(uniqIds).then((uniqRecords) => keyBy(uniqRecords, primaryKey));
    });

    const indexedResults = await Promise.all(fetchers);

    // map data
    return source.map((record) => {
      const recordWithNewFields = { ...record };
      mappers.forEach(({ fieldName, sourceKey }, index) => {
        recordWithNewFields[fieldName] = indexedResults[index][record[sourceKey]];
      });

      return recordWithNewFields;
    });
  };

  const getInitialState = () => {
    const state = collection.getInitialState();
    state.uploadedPrivateKeys = {
      byName: {},
    };
    state.selectedBackupKeys = {
      byName: {},
    };
    state.refreshKey = 0;
    state.restoreRequestsTotalCount = 0;

    return state;
  };

  const fetchAdmins = (adminIds) => {
    const promises = adminIds.map(id => UserApi.get(id));
    /**
     * UserApi.get calls are used to get admin name that has the key
     * we don't want to block the restore in case the request failed
     */
    return Promise.allSettled(promises)
      .then((promiseResults) => promiseResults
        .reduce((a, c) => (
          c.status === 'fulfilled'
            ? a.concat(c.value)
            : a), []));
  };

  const fetchDevices = (deviceIds) => (deviceIds?.length
    ? PasswordManagerDeviceApi.fetchAll(
      null, null, [PasswordManagerFilters.devices(deviceIds)],
    )
    : []);

  const fetchBackups = (backupIds) => (backupIds?.length
    ? PasswordManagerBackupApi.fetchAll(
      null, null, [PasswordManagerFilters.backups(backupIds)],
    )
    : []);

  const fetchUsers = (userIds) => (userIds?.length
    ? PasswordManagerUserApi.fetchAll(
      null, null, [PasswordManagerFilters.employees(userIds)],
    )
    : []);

  const getBackupKeysFetcher = (storeContext) => (backupKeyIds) => (backupKeyIds?.length
    ? PasswordManagerBackupKeyApi.fetchAll(null, null,
      [PasswordManagerFilters.backupKeys(backupKeyIds)]).then((backupKeys) => {
      storeContext.commit(SET_SELECTED_BACKUP_KEYS_BY_NAME, keyBy(backupKeys, 'name'));
      return backupKeys;
    })
    : []);

  const approveRequest = (restoreRequest, privateKeys) => {
    const adminPrivateKey = privateKeys.byName[restoreRequest.backup.backupKey.name].privateKey;
    const rsakey = RSAKeyPair.ImportPrivateKey(adminPrivateKey);
    const encryptionKey = rsakey.decryptWithPrivateKey(restoreRequest.backup.encryptionKey);
    const deviceRsaKey = RSAKeyPair.ImportPublicKey(
      restoreRequest.device.publicKey,
      { addHeaderAndFooter: true },
    );

    return collection.Api.update(
      {
        id: restoreRequest.id,
        status: 'APPROVED',
        // encrypt encyption key with device's public key
        encryptionKey: deviceRsaKey.encryptWithPublicKey(encryptionKey),
      },
    );
  };

  const getBackupKeys = (storeContext) => async (backupKeysIds) => {
    const backupKeys = await getBackupKeysFetcher(storeContext)(backupKeysIds);

    return mapData(backupKeys, [{
      sourceKey: 'createdBy',
      primaryKey: '_id',
      fetcher: fetchAdmins,
      fieldName: 'createdByAdmin',
    }]);
  };

  const getUsersAndKeysFetcher = (storeContext) => async (backupIds) => {
    const backups = await fetchBackups(backupIds);

    return mapData(backups, [{
      sourceKey: 'userUuid',
      primaryKey: 'employeeUuid',
      fetcher: fetchUsers,
      fieldName: 'user',
    },
    {
      sourceKey: 'backupKeyUuid',
      primaryKey: 'id',
      fetcher: getBackupKeys(storeContext),
      fieldName: 'backupKey',
    }]);
  };

  const getBackupsFetcher = (storeContext) => async (restoreRequests) => mapData(
    restoreRequests, [{
      sourceKey: 'backupUuid',
      primaryKey: 'id',
      fetcher: getUsersAndKeysFetcher(storeContext),
      fieldName: 'backup',
    }],
  );

  return {
    namespaced: true,

    state: getInitialState(),

    actions: {
      ...PasswordManagerRestoreRequestCollectionModule.actions,

      async approveRestoreRequest({ state, getters, dispatch }) {
        const { restoreRequestsToProcess } = getters;

        const requestsWithDevices = await mapData(restoreRequestsToProcess, [{
          fetcher: fetchDevices,
          fieldName: 'device',
          primaryKey: 'id',
          sourceKey: 'deviceUuid',
        }]);

        const approveRequestPromises = requestsWithDevices.map(restoreRequest => approveRequest(
          restoreRequest,
          state.uploadedPrivateKeys,
        ));

        return Promise.allSettled(approveRequestPromises).then((result) => {
          dispatch('fetchRestoreRequestsTotalCount');

          return result;
        });
      },

      async clearRestoreRequestsTotalCount({ commit }) {
        commit(SET_RESTORE_REQUEST_TOTAL_COUNT, 0);
      },

      /** overiddes CollectionModule's fetchCollection method  */
      async fetchCollection(context, { next = false } = {}) {
        let { skip } = context.state.features.items.pagination;
        const { sort, searchTerm } = context.state.features.items;
        const {
          skipToken,
          nextPageToken,
          limit,
        } = context.state.features.items.pagination;
        const filters = context.getters.getFilters;

        if (next === true && !nextPageToken && !skipToken) {
          if (collection.Api.usesPageTokens || collection.Api.usesSkipTokens) {
            return false;
          }

          skip += limit;

          if (skip >= context.state.entities.items.totalCount) {
            return false;
          }

          context.commit('SET_PAGINATION_SKIP', { skip });
        }

        const restoreRequests = await collection.Api.fetch(
          skip, limit, sort, searchTerm, null, filters, nextPageToken, skipToken,
        );

        const filledRestoreRequests = await getBackupsFetcher(context)(restoreRequests.results);

        const payload = {
          results: filledRestoreRequests,
          totalCount: restoreRequests.totalCount,
          nextPageToken: restoreRequests.nextPageToken,
          skipToken: restoreRequests.skipToken,
        };

        if (context.state.entities.items.totalCountOfAllItems === null) {
          payload.totalCountOfAllItems = restoreRequests.totalCount;
        }

        context.commit('SET_COLLECTION', payload);

        return restoreRequests.results.map(result => get(result, collection.idField));
      },

      async fetchRestoreRequestsTotalCount({ commit }) {
        const restoreRequestsTotalCount = await collection.Api
          .fetchTotalCount([PasswordManagerFilters.restoreRequestStatus('REQUESTED')]);
        commit(SET_RESTORE_REQUEST_TOTAL_COUNT, restoreRequestsTotalCount);
      },

      async rejectRestoreRequest({ state, dispatch }) {
        const selectedRequests = Object.values(state.entities.boundItems.byId);

        const rejectPromises = selectedRequests.map(restoreRequest => collection.Api.update(
          {
            id: restoreRequest.id,
            status: 'REJECTED',
          },
        ));

        return Promise.allSettled(rejectPromises).then((result) => {
          dispatch('fetchRestoreRequestsTotalCount');

          return result;
        });
      },

      resetRestoreRequestCollection({ commit }) {
        commit(RESET_RESTORE_REQUEST_COLLECTION);
        commit('RESET_COLLECTION');
      },

      /** overiddes CollectionModule's selectAllItems method  */
      selectAllItems(context, { setCollection = true } = {}) {
        return new Promise((resolve, reject) => {
          const { features } = context.state;
          const { searchTerm, filters } = features.items;

          const fetchAllItems = () => collection.Api.fetchAll(searchTerm, null, filters);

          const processAllItems = (items) => {
            if (setCollection) { context.commit('SET_COLLECTION', { results: items }); }

            const ids = items.map((item) => item.id);

            context.commit('SELECT_ITEMS', { ids });

            context.commit('ADD_BOUND_ITEMS', items);

            resolve();
          };

          fetchAllItems()
            .then((items) => getBackupsFetcher(context)(items))
            .then(processAllItems)
            .catch((error) => {
              reject(error);
            });
        });
      },

      async setUploadedPrivateKeys({ commit }, uploadedPrivateKeys) {
        commit(SET_UPLOADED_PRIVATE_KEYS, keyBy(uploadedPrivateKeys, 'name'));
      },

      updateRefreshKey(context) {
        context.commit(UPDATE_REFRESH_KEY);
      },
    },

    getters: {
      ...PasswordManagerRestoreRequestCollectionModule.getters,

      getStoredPublicKey(state) {
        return (keyName) => state.selectedBackupKeys.byName[keyName].publicKey;
      },

      refreshKey(state) {
        return state.refreshKey;
      },

      restoreRequestsToProcess(state) {
        // add reactivity dependency on selectedItems
        // eslint-disable-next-line no-unused-vars
        const selected = state.entities.boundItems.totalCount;

        const uploadedPrivateKeysByName = Object.keys(state.uploadedPrivateKeys.byName);
        return Object.values(state.entities.boundItems.byId)
          .filter(
            (restoreRequest) => uploadedPrivateKeysByName
              .includes(restoreRequest.backup.backupKey.name),
          );
      },

      restoreRequestsTotalCount(state) {
        return state.restoreRequestsTotalCount;
      },

      selectedBackupKeys(state) {
        // add reactivity dependency on selectedItems
        // eslint-disable-next-line no-unused-vars
        const selected = state.entities.boundItems.totalCount;

        const backupKeys = Object.values(state.entities.boundItems.byId).map(
          selectedRequest => selectedRequest.backup.backupKey,
        );

        return uniqBy(backupKeys, 'id');
      },
    },

    mutations: {
      ...PasswordManagerRestoreRequestCollectionModule.mutations,

      [RESET_RESTORE_REQUEST_COLLECTION](state) {
        const initialState = getInitialState();

        Vue.set(state, 'uploadedPrivateKeys', initialState.uploadedPrivateKeys);
        Vue.set(state, 'selectedBackupKeys', initialState.selectedBackupKeys);
      },

      [SET_RESTORE_REQUEST_TOTAL_COUNT](state, restoreRequestsTotalCount) {
        Vue.set(state, 'restoreRequestsTotalCount', restoreRequestsTotalCount);
      },

      [SET_SELECTED_BACKUP_KEYS_BY_NAME](state, selectedBackupKeysByName) {
        Vue.set(state.selectedBackupKeys, 'byName', selectedBackupKeysByName);
      },

      [SET_UPLOADED_PRIVATE_KEYS](state, uploadedPrivateKeys) {
        Vue.set(state.uploadedPrivateKeys, 'byName', uploadedPrivateKeys);
      },

      [UPDATE_REFRESH_KEY](state) {
        Vue.set(state, 'refreshKey', state.refreshKey + 1);
      },
    },
  };
};

export default PasswordManagerRestoreRequestCollection();
